import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Alert, Badge, Nav } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  calculatePrice, loadConfiguratorCatalog, loadPricingData,
  saveBasePriceTiers, saveFactors, saveOptionRules, saveStandardOptions,
  saveRetailOverride, savePriceListSettings, invalidatePricingCache,
  saveSidelightFamilies, saveSidelightBase, saveSidelightGlass,
  saveSidelightDividers, saveSidelightWidths, saveSidelightSettings
} from '../services/pricingService';
import { calculateDoorPrice } from '../pricing/engine';
import { optionsForFamily, defaultConfigForFamily } from '../data/configurator';

// Admin B2C Pricing — every commercial value is editable here and saved
// straight to the b2c_* tables; the public configurator picks changes up
// immediately (no deploy). Sections mirror the admin UX spec:
//   Retail Pricing | Base Prices | Retail Factors | Colours | Dimensions
//   | Glass | Locks | Thresholds | Extras | Overrides | Price Tester

const TABS = [
  ['retail', 'Retail Pricing'],
  ['base', 'Base Prices'],
  ['factors', 'Retail Factors'],
  ['colours', 'Colours'],
  ['dimensions', 'Dimensions'],
  ['glass', 'Glass'],
  ['locks', 'Locks'],
  ['thresholds', 'Thresholds'],
  ['extras', 'Extras'],
  ['sidelights', 'Sidelights'],
  ['overrides', 'Overrides'],
  ['tester', 'Price Tester']
];

const TIER_RANGES = [[1, 2], [3, 7], [8, 21], [22, 35], [36, null]];
const FACTOR_SCOPES = ['global', 'family', 'variant', 'option', 'size', 'sidelight'];
const SL_CONF_TYPES = ['SINGLE_DOOR', 'DOUBLE_DOOR', 'DOOR_LEFT_SIDELIGHT', 'DOOR_RIGHT_SIDELIGHT', 'DOOR_TWO_SIDELIGHTS'];
const SL_LAYOUTS = ['FULL_GLASS', 'ONE_HORIZONTAL_DIVIDER', 'TWO_HORIZONTAL_DIVIDERS', 'CUSTOM'];
const SL_SETTING_KEYS = [
  ['min_width_mm', 'Min width (mm)'],
  ['max_width_mm', 'Max width (mm)'],
  ['min_height_mm', 'Min height (mm)'],
  ['max_height_mm', 'Max height (mm)'],
  ['deduction_left_mm', 'Frame deduction — left (mm)'],
  ['deduction_right_mm', 'Frame deduction — right (mm)'],
  ['deduction_top_mm', 'Top rail deduction (mm)'],
  ['deduction_bottom_mm', 'Bottom rail deduction (mm)'],
  ['deduction_divider_mm', 'Glass height lost per divider (mm)']
];
const FACTOR_OPS = ['MULTIPLIER', 'PERCENT', 'FIXED_ADD', 'FIXED_SUBTRACT', 'OVERRIDE'];
const FAMILY_VARIANTS = { MERANTI: ['no_glass', 'with_glass'] };

const num = (v) => (v === '' || v == null ? null : Number(v));

const NumCell = ({ value, onChange, step = '0.01', width = '6rem' }) => (
  <Form.Control
    type="number" step={step} size="sm" value={value ?? ''}
    onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
    style={{ width }}
  />
);

const Section = ({ title, note, onSave, saving, children }) => (
  <Card className="mb-3"><Card.Body>
    <div className="d-flex justify-content-between align-items-start mb-2">
      <div>
        <Card.Title className="mb-0">{title}</Card.Title>
        {note && <div className="small text-muted mt-1">{note}</div>}
      </div>
      {onSave && (
        <Button size="sm" variant="warning" onClick={onSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      )}
    </div>
    {children}
  </Card.Body></Card>
);

const RuleTable = ({ rules, onChange, onDelete, families, showFamily = true }) => (
  <Table size="sm" bordered variant="dark" responsive>
    <thead><tr>{showFamily && <th>Family</th>}<th>Qty min–max</th><th>Op</th><th>Value</th><th>Unit</th><th>Charge scope</th><th>Label</th><th /></tr></thead>
    <tbody>
      {rules.map((r) => (
        <tr key={r.id}>
          {showFamily && (
            <td>
              <Form.Select size="sm" value={r.family_id || ''} onChange={(e) => onChange(r.id, { family_id: e.target.value || null })} style={{ width: '8.5rem' }}>
                <option value="">all</option>
                {families.map((f) => <option key={f.id} value={f.id}>{f.id}</option>)}
              </Form.Select>
            </td>
          )}
          <td>
            <div className="d-flex gap-1 align-items-center">
              <NumCell value={r.qty_min} onChange={(v) => onChange(r.id, { qty_min: v })} step="1" width="4.5rem" />–
              <NumCell value={r.qty_max} onChange={(v) => onChange(r.id, { qty_max: v })} step="1" width="4.5rem" />
            </div>
          </td>
          <td>
            <Form.Select size="sm" value={r.op} onChange={(e) => onChange(r.id, { op: e.target.value })} style={{ width: '5rem' }}>
              <option value="add">+€</option>
              <option value="pct">%</option>
            </Form.Select>
          </td>
          <td><NumCell value={r.value} onChange={(v) => onChange(r.id, { value: v })} step="1" width="6rem" /></td>
          <td>
            <Form.Select size="sm" value={r.unit} onChange={(e) => onChange(r.id, { unit: e.target.value })} style={{ width: '5.5rem' }}>
              <option value="door">door</option>
              <option value="hinge">hinge</option>
            </Form.Select>
          </td>
          <td>
            <Form.Select size="sm" value={r.charge_scope || 'PER_DOOR_LEAF'} onChange={(e) => onChange(r.id, { charge_scope: e.target.value })} style={{ width: '9.5rem' }}>
              <option value="PER_DOOR_LEAF">per leaf</option>
              <option value="PER_DOOR_SET">per set</option>
              <option value="PER_SIDELIGHT">per sidelight</option>
            </Form.Select>
          </td>
          <td><Form.Control size="sm" value={r.label || ''} onChange={(e) => onChange(r.id, { label: e.target.value })} /></td>
          <td>{onDelete && <Button size="sm" variant="outline-danger" onClick={() => onDelete(r.id)}>×</Button>}</td>
        </tr>
      ))}
    </tbody>
  </Table>
);

const PriceTester = () => {
  const [tab, setTab] = useState('retail');
  const [catalog, setCatalog] = useState(null);
  const [server, setServer] = useState(null);   // last-saved dataset (audit base)
  const [data, setData] = useState(null);       // editable draft
  const [saving, setSaving] = useState(false);
  const isBackend = data?.priceList && typeof data.priceList.id === 'number';

  const reload = async () => {
    const d = await loadPricingData();
    setServer(JSON.parse(JSON.stringify(d)));
    setData(d);
  };

  useEffect(() => {
    loadConfiguratorCatalog().then(setCatalog);
    reload();
  }, []);

  const save = async (fn, ...args) => {
    if (!isBackend) {
      toast.error('Migration 013 not applied — backend pricing tables unavailable.');
      return;
    }
    setSaving(true);
    const { error } = await fn(...args);
    setSaving(false);
    if (error) {
      toast.error(`Save failed: ${error.message}`);
    } else {
      invalidatePricingCache();
      await reload();
      toast.success('Saved — public pricing updated immediately');
    }
  };

  // ---------- draft mutators ----------
  const updTier = (familyId, variant, qtyMin, patch) => {
    setData((d) => {
      const tiers = [...d.tiers];
      const i = tiers.findIndex((t) => t.family_id === familyId && t.variant === variant && t.qty_min === qtyMin);
      if (i >= 0) tiers[i] = { ...tiers[i], ...patch };
      else tiers.push({ family_id: familyId, variant, qty_min: qtyMin, qty_max: patch.qty_max ?? null, price: null, ...patch });
      return { ...d, tiers };
    });
  };

  const updFactor = (id, patch) =>
    setData((d) => ({ ...d, factors: d.factors.map((f) => (f.id === id ? { ...f, ...patch } : f)) }));
  const delFactor = (id) =>
    setData((d) => ({ ...d, factors: d.factors.filter((f) => f.id !== id) }));

  const updRule = (id, patch) =>
    setData((d) => ({ ...d, rules: d.rules.map((r) => (r.id === id ? { ...r, ...patch } : r)) }));
  const delRule = (id) =>
    setData((d) => ({ ...d, rules: d.rules.filter((r) => r.id !== id) }));
  const addRule = (kind, target, label) =>
    setData((d) => ({
      ...d,
      rules: [...d.rules, {
        id: `new-${Date.now()}`, family_id: null, rule_kind: kind, target, meta: null,
        qty_min: null, qty_max: null, op: 'add', value: null, unit: 'door', label,
        data_status: 'verified'
      }]
    }));

  const rulesFor = (predicate) => (data?.rules || []).filter(predicate);

  const optionName = (id) => catalog?.options?.find((o) => o.id === id)?.name || id;

  // ---------- sidelight mutators ----------
  const updSl = (coll, id, patch) =>
    setData((d) => ({
      ...d,
      sidelight: {
        ...d.sidelight,
        [coll]: (d.sidelight?.[coll] || []).map((r) => (r.id === id ? { ...r, ...patch } : r))
      }
    }));
  const delSl = (coll, id) =>
    setData((d) => ({
      ...d,
      sidelight: { ...d.sidelight, [coll]: (d.sidelight?.[coll] || []).filter((r) => r.id !== id) }
    }));
  const updSlSetting = (key, value) =>
    setData((d) => ({
      ...d,
      sidelight: { ...d.sidelight, settings: { ...(d.sidelight?.settings || {}), [key]: value } }
    }));
  const updSlFamily = (familyId, enabled) =>
    setData((d) => ({
      ...d,
      sidelight: { ...d.sidelight, families: { ...(d.sidelight?.families || {}), [familyId]: enabled } }
    }));

  // ---------- sections ----------

  const renderRetail = () => {
    const pl = data.priceList;
    const global = data.factors.find((f) => f.scope === 'global');
    return (
      <>
        <Section title="PUBLIC B2C PRICING" note="Global factor and rounding apply to every calculated price. A direct override (Overrides tab) wins over everything."
          onSave={() => save(
            async () => {
              const r1 = await savePriceListSettings(pl.id, pl.rounding_mode, pl.rounding_step, server.priceList);
              if (r1.error) return r1;
              return saveFactors(pl.id, data.factors.filter((f) => f.scope === 'global'), server.factors);
            }
          )}
          saving={saving}>
          <Row>
            <Col md={4}>
              <Form.Label>Global retail factor</Form.Label>
              {global ? (
                <NumCell value={global.value} onChange={(v) => updFactor(global.id, { value: v })} step="0.01" width="8rem" />
              ) : <div className="text-muted small">No global factor — add one in Retail Factors.</div>}
            </Col>
            <Col md={4}>
              <Form.Label>Rounding mode</Form.Label>
              <Form.Select size="sm" value={pl.rounding_mode}
                onChange={(e) => setData({ ...data, priceList: { ...pl, rounding_mode: e.target.value } })}>
                <option value="NONE">None</option>
                <option value="NEAREST">Nearest</option>
                <option value="UP">Always round up</option>
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label>Rounding step (€)</Form.Label>
              <NumCell value={pl.rounding_step} onChange={(v) => setData({ ...data, priceList: { ...pl, rounding_step: v } })} step="1" width="8rem" />
            </Col>
          </Row>
          <div className="small text-muted mt-2">
            Price list: <strong>{pl.code} v{pl.version}</strong> · {pl.currency}
            {!isBackend && ' · seed fallback — apply migration 013 to save'}
          </div>
        </Section>
      </>
    );
  };

  const renderBasePrices = () => (
    <Section title="Base prices" note="Per-item quantity pricing per family and variant. Empty price = PRICE_REQUIRES_CONFIGURATION."
      onSave={() => save(saveBasePriceTiers, data.priceList.id, data.tiers, server.tiers)}
      saving={saving}>
      {catalog?.families.map((f) => {
        const variants = FAMILY_VARIANTS[f.id] || ['default'];
        return variants.map((variant) => (
          <div key={`${f.id}-${variant}`} className="mb-3">
            <div className="fw-semibold mb-1">
              {f.name} <span className="text-muted small">({f.id}{variant !== 'default' ? ` · ${variant}` : ''})</span>
            </div>
            <Table size="sm" bordered variant="dark" responsive style={{ maxWidth: '36rem' }}>
              <thead><tr><th>Qty</th><th>Price €</th></tr></thead>
              <tbody>
                {TIER_RANGES.map(([min, max]) => {
                  const t = data.tiers.find((x) => x.family_id === f.id && x.variant === variant && x.qty_min === min);
                  return (
                    <tr key={min}>
                      <td>{min}–{max ?? '+'}</td>
                      <td><NumCell value={t?.price} onChange={(v) => updTier(f.id, variant, min, { qty_max: max, price: v })} step="1" width="7rem" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        ));
      })}
    </Section>
  );

  const scopeKeyEditor = (f) => {
    if (f.scope === 'family') {
      return (
        <Form.Select size="sm" value={f.scope_key || ''} onChange={(e) => updFactor(f.id, { scope_key: e.target.value })} style={{ width: '10rem' }}>
          <option value="">—</option>
          {catalog?.families.map((fam) => <option key={fam.id} value={fam.id}>{fam.id}</option>)}
        </Form.Select>
      );
    }
    if (f.scope === 'option') {
      return (
        <Form.Select size="sm" value={f.scope_key || ''} onChange={(e) => updFactor(f.id, { scope_key: e.target.value })} style={{ width: '12rem' }}>
          <option value="">—</option>
          {catalog?.options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </Form.Select>
      );
    }
    if (f.scope === 'global') return <span className="text-muted small">all</span>;
    return (
      <Form.Control size="sm" value={f.scope_key || ''} placeholder={f.scope === 'variant' ? 'e.g. with_glass' : 'key'}
        onChange={(e) => updFactor(f.id, { scope_key: e.target.value })} style={{ width: '10rem' }} />
    );
  };

  const renderFactors = () => (
    <Section title="Retail factors" note="Applied in precedence: global → family → variant → option → size. OVERRIDE ends factor processing."
      onSave={() => save(saveFactors, data.priceList.id, data.factors, server.factors)}
      saving={saving}>
      <Table size="sm" bordered variant="dark" responsive>
        <thead><tr><th>Scope</th><th>Key</th><th>Operation</th><th>Value</th><th>Label</th><th>On</th><th /></tr></thead>
        <tbody>
          {data.factors.map((f) => (
            <tr key={f.id}>
              <td>
                <Form.Select size="sm" value={f.scope} onChange={(e) => updFactor(f.id, { scope: e.target.value, scope_key: null })} style={{ width: '7rem' }}>
                  {FACTOR_SCOPES.map((s) => <option key={s} value={s}>{s}</option>)}
                </Form.Select>
              </td>
              <td>{scopeKeyEditor(f)}</td>
              <td>
                <Form.Select size="sm" value={f.operation} onChange={(e) => updFactor(f.id, { operation: e.target.value })} style={{ width: '9.5rem' }}>
                  {FACTOR_OPS.map((o) => <option key={o} value={o}>{o}</option>)}
                </Form.Select>
              </td>
              <td><NumCell value={f.value} onChange={(v) => updFactor(f.id, { value: v })} step="0.01" width="6.5rem" /></td>
              <td><Form.Control size="sm" value={f.label || ''} onChange={(e) => updFactor(f.id, { label: e.target.value })} /></td>
              <td><Form.Check checked={!!f.enabled} onChange={(e) => updFactor(f.id, { enabled: e.target.checked })} /></td>
              <td><Button size="sm" variant="outline-danger" onClick={() => delFactor(f.id)}>×</Button></td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Button size="sm" variant="outline-warning" onClick={() => setData((d) => ({
        ...d,
        factors: [...d.factors, { id: `new-${Date.now()}`, scope: 'option', scope_key: null, operation: 'MULTIPLIER', value: 1, label: '', enabled: true, sort: 30 }]
      }))}>+ Add factor</Button>
      <div className="small text-muted mt-2">Deleted rows are removed on Save. Effective multiplier example: GLOBAL ×1.20 × FAMILY ×1.05 = ×1.26.</div>
    </Section>
  );

  const renderColours = () => {
    const colourOpts = (catalog?.options || []).filter((o) => o.group_key === 'colour');
    const colourRules = rulesFor((r) => r.target === 'colour_nonstandard');
    return (
      <>
        <Section title="Standard colours" note="Selected colours carry no surcharge; every other colour uses the tiers below (10+ doors → manual review)."
          onSave={() => save(saveStandardOptions, data.priceList.id, data.standardOptions, server.standardOptions)}
          saving={saving}>
          {colourOpts.map((o) => (
            <Form.Check
              key={o.id} inline label={o.name}
              checked={data.standardOptions.includes(o.id)}
              onChange={(e) => setData({
                ...data,
                standardOptions: e.target.checked
                  ? [...data.standardOptions, o.id]
                  : data.standardOptions.filter((x) => x !== o.id)
              })}
            />
          ))}
        </Section>
        <Section title="Non-standard colour tiers" note="Per-door surcharge when the colour is not in the standard set."
          onSave={() => save(saveOptionRules, data.priceList.id, data.rules.filter((r) => r.target === 'colour_nonstandard'), server.rules)}
          saving={saving}>
          <RuleTable rules={colourRules} onChange={updRule} onDelete={delRule} families={catalog?.families || []} showFamily={false} />
        </Section>
      </>
    );
  };

  const renderDimensions = () => {
    const dimRules = rulesFor((r) => r.rule_kind === 'dimension');
    const sizeRules = rulesFor((r) => r.rule_kind === 'special_size');
    return (
      <Section title="Dimensions" note="Special heights use a fixed surcharge; out-of-range sizes use the percentage rule."
        onSave={() => save(saveOptionRules, data.priceList.id,
          data.rules.filter((r) => r.rule_kind === 'dimension' || r.rule_kind === 'special_size'), server.rules)}
        saving={saving}>
        <div className="fw-semibold mb-1">Special heights (mm)</div>
        {dimRules.map((r) => (
          <div key={r.id} className="mb-2">
            <div className="d-flex gap-2 align-items-center flex-wrap">
              <Form.Control
                size="sm" style={{ width: '10rem' }}
                value={(r.meta?.heights || []).join(', ')}
                placeholder="2115, 2118"
                onChange={(e) => updRule(r.id, { meta: { ...r.meta, heights: e.target.value.split(',').map((x) => Number(x.trim())).filter(Boolean) } })}
              />
              <span className="text-muted small">+</span>
              <NumCell value={r.value} onChange={(v) => updRule(r.id, { value: v })} step="1" width="5.5rem" />
              <span className="text-muted small">€/door</span>
              <Form.Control size="sm" value={r.label || ''} onChange={(e) => updRule(r.id, { label: e.target.value })} style={{ width: '12rem' }} />
              <Button size="sm" variant="outline-danger" onClick={() => delRule(r.id)}>×</Button>
            </div>
          </div>
        ))}
        <Button size="sm" variant="outline-warning" className="mb-3" onClick={() => addRule('dimension', 'height_special', 'Special height')}>+ Add height rule</Button>
        <div className="fw-semibold mb-1">Non-standard dimensions</div>
        <RuleTable rules={sizeRules} onChange={updRule} onDelete={delRule} families={catalog?.families || []} showFamily />
      </Section>
    );
  };

  const optionRuleSection = (title, note, targets, { allowAdd = false, targetPrefix = '' } = {}) => {
    const rules = rulesFor((r) => r.rule_kind === 'option' && targets.includes(r.target));
    return (
      <Section title={title} note={note}
        onSave={() => save(saveOptionRules, data.priceList.id, data.rules.filter((r) => r.rule_kind === 'option' && targets.includes(r.target)), server.rules)}
        saving={saving}>
        {targets.map((target) => {
          const rows = rules.filter((r) => r.target === target);
          if (!rows.length) return null;
          return (
            <div key={target} className="mb-3">
              <div className="fw-semibold mb-1">{optionName(target)} <span className="text-muted small">({target})</span></div>
              <RuleTable rules={rows} onChange={updRule} onDelete={delRule} families={catalog?.families || []} />
            </div>
          );
        })}
        {allowAdd && (
          <Form.Select size="sm" value="" onChange={(e) => e.target.value && addRule('option', e.target.value, optionName(e.target.value))} style={{ width: '16rem' }}>
            <option value="">+ Add rule for…</option>
            {(catalog?.options || []).filter((o) => o.id.startsWith(targetPrefix) && !targets.includes(o.id)).map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </Form.Select>
        )}
      </Section>
    );
  };

  const targetsOfKind = (prefix) =>
    [...new Set((data?.rules || []).filter((r) => r.rule_kind === 'option' && r.target.startsWith(prefix)).map((r) => r.target))];

  // ---------- sidelights ----------

  const renderSidelights = () => {
    const sl = data.sidelight || {};
    const famRows = catalog.families.map((f) => ({ family_id: f.id, enabled: !!sl.families?.[f.id] }));
    const settings = sl.settings || {};
    return (
      <>
        <Section title="Family support" note="Only enabled families offer sidelights publicly. A forced request for a disabled family returns requiresReview — no silent pricing."
          onSave={() => save(saveSidelightFamilies, data.priceList.id, famRows, [])}
          saving={saving}>
          {famRows.map((r) => (
            <Form.Check key={r.family_id} inline label={r.family_id}
              checked={r.enabled} onChange={(e) => updSlFamily(r.family_id, e.target.checked)} />
          ))}
        </Section>

        <Section title="Structural base — € per sidelight" note="Empty price = requiresReview until Hestia enters real values. A family row wins over the generic (all) row."
          onSave={() => save(saveSidelightBase, data.priceList.id, sl.base || [], server?.sidelight?.base)}
          saving={saving}>
          <Table size="sm" bordered variant="dark" responsive style={{ maxWidth: '44rem' }}>
            <thead><tr><th>Family</th><th>Variant</th><th>Base €</th><th>Enabled</th><th /></tr></thead>
            <tbody>
              {(sl.base || []).map((b) => (
                <tr key={b.id}>
                  <td>
                    <Form.Select size="sm" value={b.family_id || ''} onChange={(e) => updSl('base', b.id, { family_id: e.target.value || null })} style={{ width: '9rem' }}>
                      <option value="">all (generic)</option>
                      {catalog.families.map((f) => <option key={f.id} value={f.id}>{f.id}</option>)}
                    </Form.Select>
                  </td>
                  <td><Form.Control size="sm" value={b.variant || 'default'} onChange={(e) => updSl('base', b.id, { variant: e.target.value })} style={{ width: '7rem' }} /></td>
                  <td><NumCell value={b.base_price} onChange={(v) => updSl('base', b.id, { base_price: v, data_status: v == null ? 'placeholder' : 'verified' })} step="1" width="6rem" /></td>
                  <td><Form.Check checked={b.enabled !== false} onChange={(e) => updSl('base', b.id, { enabled: e.target.checked })} /></td>
                  <td><Button size="sm" variant="outline-danger" onClick={() => delSl('base', b.id)}>×</Button></td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Button size="sm" variant="outline-warning" onClick={() => setData((d) => ({
            ...d,
            sidelight: { ...d.sidelight, base: [...(d.sidelight?.base || []), { id: `new-${Date.now()}`, family_id: null, variant: 'default', base_price: null, enabled: true }] }
          }))}>+ Add base row</Button>
        </Section>

        <Section title="Glass — €/m²" note="Price per effective glazing area (after frame deductions). Empty = unpriced → requiresReview. Disabled types are hidden publicly."
          onSave={() => save(saveSidelightGlass, data.priceList.id, sl.glass || [], server?.sidelight?.glass)}
          saving={saving}>
          <Table size="sm" bordered variant="dark" responsive>
            <thead><tr><th>Type</th><th>Public name</th><th>Name ET</th><th>€/m²</th><th>Enabled</th><th>Sort</th><th /></tr></thead>
            <tbody>
              {(sl.glass || []).map((g) => (
                <tr key={g.id}>
                  <td><Form.Control size="sm" value={g.glass_type} onChange={(e) => updSl('glass', g.id, { glass_type: e.target.value.toUpperCase() })} style={{ width: '9rem' }} /></td>
                  <td><Form.Control size="sm" value={g.name || ''} onChange={(e) => updSl('glass', g.id, { name: e.target.value })} /></td>
                  <td><Form.Control size="sm" value={g.name_et || ''} onChange={(e) => updSl('glass', g.id, { name_et: e.target.value })} /></td>
                  <td><NumCell value={g.price_per_m2} onChange={(v) => updSl('glass', g.id, { price_per_m2: v })} step="1" width="6rem" /></td>
                  <td><Form.Check checked={g.enabled !== false} onChange={(e) => updSl('glass', g.id, { enabled: e.target.checked })} /></td>
                  <td><NumCell value={g.sort} onChange={(v) => updSl('glass', g.id, { sort: v })} step="1" width="4rem" /></td>
                  <td><Button size="sm" variant="outline-danger" onClick={() => delSl('glass', g.id)}>×</Button></td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Button size="sm" variant="outline-warning" onClick={() => setData((d) => ({
            ...d,
            sidelight: { ...d.sidelight, glass: [...(d.sidelight?.glass || []), { id: `new-${Date.now()}`, glass_type: 'CUSTOM', name: '', name_et: '', price_per_m2: null, enabled: false, sort: 90 }] }
          }))}>+ Add glass type</Button>
        </Section>

        <Section title="Timber dividers" note="€ per divider, or € per linear metre (divider length = sidelight width). Empty = unpriced → requiresReview."
          onSave={() => save(saveSidelightDividers, data.priceList.id, sl.dividers || [], server?.sidelight?.dividers)}
          saving={saving}>
          <Table size="sm" bordered variant="dark" responsive style={{ maxWidth: '44rem' }}>
            <thead><tr><th>Type</th><th>Name</th><th>€/pc</th><th>€/m</th><th>Enabled</th></tr></thead>
            <tbody>
              {(sl.dividers || []).map((dv) => (
                <tr key={dv.id}>
                  <td className="small">{dv.divider_type}</td>
                  <td><Form.Control size="sm" value={dv.name || ''} onChange={(e) => updSl('dividers', dv.id, { name: e.target.value })} /></td>
                  <td><NumCell value={dv.fixed_price} onChange={(v) => updSl('dividers', dv.id, { fixed_price: v })} step="1" width="6rem" /></td>
                  <td><NumCell value={dv.price_per_lm} onChange={(v) => updSl('dividers', dv.id, { price_per_lm: v })} step="1" width="6rem" /></td>
                  <td><Form.Check checked={dv.enabled !== false} onChange={(e) => updSl('dividers', dv.id, { enabled: e.target.checked })} /></td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Section>

        <Section title="Standard widths" note="Widths offered as one-click options in the public UI. Other in-range widths are 'custom' — priced automatically or flagged for review per the mode below."
          onSave={() => save(saveSidelightWidths, data.priceList.id, sl.widths || [], server?.sidelight?.widths)}
          saving={saving}>
          <div className="d-flex gap-2 align-items-center flex-wrap">
            {(sl.widths || []).map((wd) => (
              <div key={wd.id} className="d-flex align-items-center gap-1 border rounded px-2 py-1">
                <Form.Check checked={wd.enabled !== false} onChange={(e) => updSl('widths', wd.id, { enabled: e.target.checked })} />
                <NumCell value={wd.width_mm} onChange={(v) => updSl('widths', wd.id, { width_mm: v })} step="10" width="5.5rem" />
                <span className="small text-muted">mm</span>
                <Button size="sm" variant="outline-danger" className="py-0 px-1" onClick={() => delSl('widths', wd.id)}>×</Button>
              </div>
            ))}
            <Button size="sm" variant="outline-warning" onClick={() => setData((d) => ({
              ...d,
              sidelight: { ...d.sidelight, widths: [...(d.sidelight?.widths || []), { id: `new-${Date.now()}`, width_mm: 500, enabled: true, sort: 90 }] }
            }))}>+ width</Button>
          </div>
        </Section>

        <Section title="Limits, deductions & special sizes" note="Deductions are ASSUMED geometry (frame member ≈ 40 mm) pending Hestia production confirmation. Special-size mode decides what happens outside min/max."
          onSave={() => save(saveSidelightSettings, data.priceList.id, settings, server?.sidelight?.settings)}
          saving={saving}>
          <Row>
            {SL_SETTING_KEYS.map(([key, label]) => (
              <Col md={4} className="mb-2" key={key}>
                <Form.Label className="small">{label}</Form.Label>
                <NumCell value={Number(settings[key])} onChange={(v) => updSlSetting(key, v)} step="1" width="7rem" />
              </Col>
            ))}
            <Col md={4} className="mb-2">
              <Form.Label className="small">Custom width mode</Form.Label>
              <Form.Select size="sm" value={settings.custom_width_mode || 'manual'} onChange={(e) => updSlSetting('custom_width_mode', e.target.value)} style={{ width: '10rem' }}>
                <option value="manual">manual → review</option>
                <option value="priced">priced → auto</option>
              </Form.Select>
            </Col>
            <Col md={4} className="mb-2">
              <Form.Label className="small">Special size op</Form.Label>
              <Form.Select size="sm" value={settings.special_size_op || 'manual'} onChange={(e) => updSlSetting('special_size_op', e.target.value)} style={{ width: '10rem' }}>
                <option value="manual">manual → review</option>
                <option value="add">+ € fixed</option>
                <option value="pct">+ % of component</option>
              </Form.Select>
            </Col>
            <Col md={4} className="mb-2">
              <Form.Label className="small">Special size value</Form.Label>
              <NumCell value={settings.special_size_value == null ? null : Number(settings.special_size_value)} onChange={(v) => updSlSetting('special_size_value', v)} step="1" width="7rem" />
            </Col>
          </Row>
          <div className="small text-muted">Sidelight retail margin: add a factor with scope <code>sidelight</code> in Retail Factors — it applies to sidelight subtotals only, on top of the global factor.</div>
        </Section>
      </>
    );
  };

  // ---------- tester ----------
  const [tFamily, setTFamily] = useState('18_DEG');
  const [tQty, setTQty] = useState(1);
  const [tW, setTW] = useState(900);
  const [tH, setTH] = useState(2000);
  const [tConfig, setTConfig] = useState({});
  const [tExtras, setTExtras] = useState([]);
  const [tResult, setTResult] = useState(null);
  const [tConfType, setTConfType] = useState('SINGLE_DOOR');
  const [tSlLeft, setTSlLeft] = useState({ width_mm: 400, glass_type: 'CLEAR', layout: 'FULL_GLASS' });
  const [tSlRight, setTSlRight] = useState({ width_mm: 400, glass_type: 'CLEAR', layout: 'FULL_GLASS' });

  const tFamObj = useMemo(() => catalog?.families.find((f) => f.id === tFamily), [catalog, tFamily]);
  const tGrouped = useMemo(
    () => (tFamObj ? optionsForFamily(tFamObj.id, catalog.options, catalog.compatibility) : {}),
    [catalog, tFamObj]
  );
  useEffect(() => {
    if (tFamObj) {
      setTConfig(defaultConfigForFamily(tFamObj.id, catalog.options, catalog.compatibility));
      setTExtras([]);
      setTW(tFamObj.refWidth); setTH(tFamObj.refHeight);
    }
  }, [tFamObj, catalog]);

  const runTest = async () => {
    const input = {
      family: tFamily, qty: Number(tQty), width_mm: Number(tW), height_mm: Number(tH),
      design: tConfType === 'DOUBLE_DOOR' ? 'design_double' : tConfig.design,
      colour: tConfig.colour, glass: tConfig.glass,
      frame: tConfig.frame, threshold: tConfig.threshold, lock: tConfig.lock,
      hinge: tConfig.hinge,
      configuration_type: tConfType,
      left_sidelight: ['DOOR_LEFT_SIDELIGHT', 'DOOR_TWO_SIDELIGHTS'].includes(tConfType) ? tSlLeft : null,
      right_sidelight: ['DOOR_RIGHT_SIDELIGHT', 'DOOR_TWO_SIDELIGHTS'].includes(tConfType) ? tSlRight : null,
      hinges: tConfig.hinge === 'hinge_2' ? 2 : 3,
      extras: tExtras, opening: tFamObj.opening === 'both' ? 'outward' : tFamObj.opening, handing: 'left'
    };
    const res = await calculatePrice(input, { debug: true });
    if (res.breakdown) {
      setTResult(res);
    } else {
      const local = calculateDoorPrice(input, data);
      setTResult({ ...local, source: 'local-fallback' });
    }
  };

  const renderTester = () => (
    <Row>
      <Col md={5}>
        <Section title="Test configuration">
          <Form.Group className="mb-2">
            <Form.Label>Family</Form.Label>
            <Form.Select size="sm" value={tFamily} onChange={(e) => setTFamily(e.target.value)}>
              {catalog?.families.map((f) => <option key={f.id} value={f.id}>{f.name} ({f.id})</option>)}
            </Form.Select>
          </Form.Group>
          <Row>
            <Col><Form.Group className="mb-2"><Form.Label>Qty</Form.Label>
              <Form.Control size="sm" type="number" min={1} value={tQty} onChange={(e) => setTQty(e.target.value)} /></Form.Group></Col>
            <Col><Form.Group className="mb-2"><Form.Label>Width mm</Form.Label>
              <Form.Control size="sm" type="number" value={tW} onChange={(e) => setTW(e.target.value)} /></Form.Group></Col>
            <Col><Form.Group className="mb-2"><Form.Label>Height mm</Form.Label>
              <Form.Control size="sm" type="number" value={tH} onChange={(e) => setTH(e.target.value)} /></Form.Group></Col>
          </Row>
          <Form.Group className="mb-2">
            <Form.Label>Configuration type</Form.Label>
            <Form.Select size="sm" value={tConfType} onChange={(e) => setTConfType(e.target.value)}>
              {SL_CONF_TYPES.map((ct) => <option key={ct} value={ct}>{ct}</option>)}
            </Form.Select>
          </Form.Group>
          {tConfType.includes('SIDELIGHT') && (['left', 'right']).map((side) => {
            const on = side === 'left'
              ? ['DOOR_LEFT_SIDELIGHT', 'DOOR_TWO_SIDELIGHTS'].includes(tConfType)
              : ['DOOR_RIGHT_SIDELIGHT', 'DOOR_TWO_SIDELIGHTS'].includes(tConfType);
            if (!on) return null;
            const val = side === 'left' ? tSlLeft : tSlRight;
            const set = side === 'left' ? setTSlLeft : setTSlRight;
            const glassTypes = data.sidelight?.glass?.length ? data.sidelight.glass : [{ glass_type: 'CLEAR' }, { glass_type: 'FROSTED' }];
            return (
              <div key={side} className="border rounded p-2 mb-2">
                <div className="small fw-semibold mb-1 text-capitalize">{side} sidelight</div>
                <Row>
                  <Col><Form.Label className="small">Width mm</Form.Label>
                    <Form.Control size="sm" type="number" value={val.width_mm} onChange={(e) => set({ ...val, width_mm: Number(e.target.value) })} /></Col>
                  <Col><Form.Label className="small">Height mm (∅=door)</Form.Label>
                    <Form.Control size="sm" type="number" value={val.height_mm || ''} onChange={(e) => set({ ...val, height_mm: e.target.value ? Number(e.target.value) : undefined })} /></Col>
                </Row>
                <Row className="mt-1">
                  <Col><Form.Label className="small">Glass</Form.Label>
                    <Form.Select size="sm" value={val.glass_type} onChange={(e) => set({ ...val, glass_type: e.target.value })}>
                      {glassTypes.map((g) => <option key={g.glass_type} value={g.glass_type}>{g.name || g.glass_type}</option>)}
                    </Form.Select></Col>
                  <Col><Form.Label className="small">Layout</Form.Label>
                    <Form.Select size="sm" value={val.layout} onChange={(e) => set({ ...val, layout: e.target.value })}>
                      {SL_LAYOUTS.map((l) => <option key={l} value={l}>{l}</option>)}
                    </Form.Select></Col>
                </Row>
              </div>
            );
          })}
          {['design', 'colour', 'glass', 'frame', 'threshold', 'lock', 'hinge', 'sidelight'].map((g) => tGrouped[g]?.length ? (
            <Form.Group className="mb-2" key={g}>
              <Form.Label className="text-capitalize">{g}</Form.Label>
              <Form.Select size="sm" value={tConfig[g] || ''} onChange={(e) => setTConfig({ ...tConfig, [g]: e.target.value })}>
                {tGrouped[g].map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </Form.Select>
            </Form.Group>
          ) : null)}
          {tGrouped.extra?.length > 0 && (
            <Form.Group className="mb-2">
              <Form.Label>Extras</Form.Label>
              {tGrouped.extra.map((o) => (
                <Form.Check key={o.id} label={o.name} checked={tExtras.includes(o.id)}
                  onChange={() => setTExtras(tExtras.includes(o.id) ? tExtras.filter((x) => x !== o.id) : [...tExtras, o.id])} />
              ))}
            </Form.Group>
          )}
          <Button variant="warning" onClick={runTest}>Calculate</Button>
        </Section>
      </Col>
      <Col md={7}>
        <Section title="Result — staged breakdown" note="Admin-only: the public configurator never sees this detail.">
          {!tResult ? <p className="text-muted">Run a calculation.</p> : (
            <>
              <p className="mb-2">
                <Badge bg={tResult.source === 'backend' ? 'success' : 'warning'} className="me-2">{tResult.source}</Badge>
                <Badge bg={tResult.confidence === 'high' ? 'success' : tResult.confidence === 'low' ? 'warning' : 'danger'} className="me-2">{tResult.confidence}</Badge>
                {tResult.requiresReview && <Badge bg="danger">requiresReview</Badge>}
                <span className="ms-2 small text-muted">{tResult.priceList?.code} v{tResult.priceList?.version}{tResult.variant !== 'default' ? ` · ${tResult.variant}` : ''}</span>
              </p>
              <h4>{tResult.retailPrice != null ? `€${tResult.retailPrice} / unit` : '—'}{tResult.totalPrice != null && tResult.qty > 1 ? ` · total €${tResult.totalPrice} (×${tResult.qty})` : ''}</h4>
              {tResult.reasons?.length > 0 && (
                <Alert variant="warning" className="small py-2">{tResult.reasons.map((r) => <div key={r}>• {r}</div>)}</Alert>
              )}
              {tResult.breakdown?.length > 0 && (
                <Table size="sm" striped bordered variant="dark">
                  <thead><tr><th>Stage</th><th>Component</th><th>Rule</th><th>Detail</th><th className="text-end">Amount</th></tr></thead>
                  <tbody>
                    {tResult.breakdown.map((b, i) => (
                      <tr key={i} className={b.step === 'subtotal' || b.step === 'component_subtotal' ? 'table-warning' : ''}>
                        <td className="small">{b.stage}</td>
                        <td className="small text-muted">{b.component || ''}</td>
                        <td>{b.label || b.step}</td>
                        <td className="small text-muted">
                          {b.op === 'pct' ? `+${b.value}%` : b.op === 'add' ? `+€${b.value}${b.unit === 'hinge' ? ' × hinges' : ''}` : ''}
                          {b.operation ? ` ${b.operation} ${b.value}` : ''}
                          {b.scope ? ` (${b.scope}${b.scopeKey ? ':' + b.scopeKey : ''})` : ''}
                          {b.reason ? ` ${b.reason}` : ''}
                          {b.tierId ? ` #${b.tierId}` : ''}{b.ruleId ? ` #${b.ruleId}` : ''}
                          {b.factorId ? ` #${b.factorId}` : ''}{b.overrideId ? ` #${b.overrideId}` : ''}
                        </td>
                        <td className="text-end">{b.amount != null ? `€${b.amount}` : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </>
          )}
        </Section>
      </Col>
    </Row>
  );

  const [ovForm, setOvForm] = useState({ family: '60_OUT', variant: 'default', qtyMin: 1, qtyMax: '', price: '', note: '' });
  const renderOverrides = () => (
    <Section title="Direct B2C price overrides" note="An override replaces the calculated retail price entirely (no factors, no rounding). History is kept automatically."
      saving={saving}>
      <Table size="sm" bordered variant="dark" responsive>
        <thead><tr><th>Family</th><th>Variant</th><th>Qty</th><th>Price</th><th>Note</th><th>Active</th><th>Created</th></tr></thead>
        <tbody>
          {(data.overrides || []).map((o) => (
            <tr key={o.id} className={o.active ? '' : 'text-muted'}>
              <td>{o.family_id}</td><td>{o.variant}</td>
              <td>{o.qty_min}–{o.qty_max ?? '+'}</td>
              <td>€{o.price}</td><td>{o.note || ''}</td>
              <td>{o.active ? '✓' : '—'}</td>
              <td className="small">{String(o.created_at || '').slice(0, 10)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      <div className="d-flex gap-2 align-items-end flex-wrap mt-2">
        <div><Form.Label className="small">Family</Form.Label>
          <Form.Select size="sm" value={ovForm.family} onChange={(e) => setOvForm({ ...ovForm, family: e.target.value })} style={{ width: '9rem' }}>
            {catalog?.families.map((f) => <option key={f.id} value={f.id}>{f.id}</option>)}
          </Form.Select></div>
        <div><Form.Label className="small">Variant</Form.Label>
          <Form.Control size="sm" value={ovForm.variant} onChange={(e) => setOvForm({ ...ovForm, variant: e.target.value })} style={{ width: '7rem' }} /></div>
        <div><Form.Label className="small">Qty min</Form.Label>
          <Form.Control size="sm" type="number" value={ovForm.qtyMin} onChange={(e) => setOvForm({ ...ovForm, qtyMin: e.target.value })} style={{ width: '5rem' }} /></div>
        <div><Form.Label className="small">Qty max</Form.Label>
          <Form.Control size="sm" type="number" value={ovForm.qtyMax} onChange={(e) => setOvForm({ ...ovForm, qtyMax: e.target.value })} style={{ width: '5rem' }} placeholder="∞" /></div>
        <div><Form.Label className="small">Price €</Form.Label>
          <Form.Control size="sm" type="number" value={ovForm.price} onChange={(e) => setOvForm({ ...ovForm, price: e.target.value })} style={{ width: '6rem' }} /></div>
        <div><Form.Label className="small">Note</Form.Label>
          <Form.Control size="sm" value={ovForm.note} onChange={(e) => setOvForm({ ...ovForm, note: e.target.value })} style={{ width: '12rem' }} /></div>
        <Button size="sm" variant="warning" disabled={!ovForm.price} onClick={() => save(
          saveRetailOverride, data.priceList.id, ovForm.family, ovForm.variant || 'default',
          Number(ovForm.qtyMin) || 1, num(ovForm.qtyMax), Number(ovForm.price), ovForm.note,
          (server.overrides || []).find((o) => o.family_id === ovForm.family && o.variant === ovForm.variant && o.active)
        )}>Add override</Button>
      </div>
    </Section>
  );

  if (!data || !catalog) return <Container className="py-4"><p>Loading pricing data…</p></Container>;

  return (
    <Container fluid className="py-4" style={{ maxWidth: '1200px' }}>
      <h2 className="mb-1">Retail Pricing</h2>
      <Alert variant="secondary" className="small py-2">
        Every change saves to Supabase and applies to the public configurator immediately.
        {!isBackend && <strong className="text-warning"> Currently showing seed fallback — migration 013 not detected; saves will fail until applied.</strong>}
      </Alert>
      <Nav variant="tabs" activeKey={tab} onSelect={setTab} className="mb-3" style={{ flexWrap: 'wrap' }}>
        {TABS.map(([key, label]) => (
          <Nav.Item key={key}><Nav.Link eventKey={key} className="py-2 px-3">{label}</Nav.Link></Nav.Item>
        ))}
      </Nav>

      {tab === 'retail' && renderRetail()}
      {tab === 'base' && renderBasePrices()}
      {tab === 'factors' && renderFactors()}
      {tab === 'colours' && renderColours()}
      {tab === 'dimensions' && renderDimensions()}
      {tab === 'glass' && optionRuleSection('Glass', 'Per-door glass surcharges; quantity-tiered where the source defines tiers.', targetsOfKind('glass_'), { allowAdd: true, targetPrefix: 'glass_' })}
      {tab === 'locks' && optionRuleSection('Locks', 'Per-door lock surcharges.', targetsOfKind('lock_'), { allowAdd: true, targetPrefix: 'lock_' })}
      {tab === 'thresholds' && optionRuleSection('Thresholds', 'Family-scoped threshold surcharges.', targetsOfKind('threshold_'), { allowAdd: true, targetPrefix: 'threshold_' })}
      {tab === 'extras' && optionRuleSection('Extras', 'Per-door or per-hinge extras.', targetsOfKind('extra_'), { allowAdd: true, targetPrefix: 'extra_' })}
      {tab === 'sidelights' && renderSidelights()}
      {tab === 'overrides' && renderOverrides()}
      {tab === 'tester' && renderTester()}
    </Container>
  );
};

export default PriceTester;
